import type { Lesson, MeResponse } from "@edu/shared";
import { dayCountText } from "../../lib/plurals.ts";
import { TRASH_KEEP_DAYS } from "../homework/trash-keep.ts";

/**
 * Звонок с преподавателями в кабинете: строка списка «Уроки», кнопка создания
 * и слова (разделы 9.1, 9.2 и 9.4 `docs/teacher-calls-design.md`). Чистые
 * правила без React, чтобы проверять их встроенным `node:test`.
 *
 * ⚠️ **Звонок отличается от урока полем `call`, а не ролью.** У гостя `myRole`
 * это `student` (права гостя это права ученика), и правило, ветвящееся по
 * одной роли, подписало бы преподавателя, пришедшего по ссылке, «вы ученик», а
 * ведущему показало бы «без ученика». Поэтому каждое правило строки сначала
 * спрашивает `call`, и только потом роль.
 *
 * ⚠️ Модуль лежит в стартовом чанке вместе со строкой списка, и это по проекту:
 * в нём нет ни комнаты, ни доски. Из `features/room` и `features/board` он не
 * импортирует ничего (граница чанков, 6.4 проекта); тексты комнаты для звонка
 * живут там, в `room/call-words.ts`.
 */

/**
 * Видна ли кнопка «Звонок с преподавателями».
 *
 * Право приезжает только полной сессии держателя (`MeResponse.teacherCalls`), а
 * в просмотре кабинета админом кнопки нет, даже если у просматриваемого право
 * есть: ручка создания такой сессии закрыта замком записи, и кнопка вела бы в
 * отказ.
 */
export function mayCreateCall(me: Pick<MeResponse, "teacherCalls" | "viewer">): boolean {
  return me.teacherCalls === true && me.viewer == null;
}

/** Название звонка в окне создания по умолчанию. */
export const CALL_DEFAULT_TITLE = "Звонок с преподавателями";

/** Слово в колонке «Ученик» у строки звонка, у ведущего и у гостя. */
export const CALL_WHO = "звонок";

/**
 * Что написано в колонке «Ученик» вместо имени, либо `null`: решает обычная
 * колонка урока (имя, группа, «без ученика»).
 */
export function callWhoOf(lesson: Pick<Lesson, "call">): string | null {
  return lesson.call === undefined ? null : CALL_WHO;
}

/**
 * Подпись под названием в строке списка, либо `null`.
 *
 * У гостя это имя того, кто ведёт: звонок ему завёл не он, и без подписи строка
 * не говорит, чей это звонок. Слово «звонок» здесь не повторяется: оно уже
 * стоит в колонке рядом (`callWhoOf`), а на узкой строке та колонка становится
 * второй подписью под тем же названием.
 */
export function rowSubOf(lesson: Pick<Lesson, "myRole" | "call">): string | null {
  if (lesson.call !== undefined) {
    const host = lesson.call.hostName;
    const named = host !== undefined && host !== "";
    return lesson.call.role === "guest" && named ? `ведёт ${host}` : null;
  }
  return lesson.myRole === "teacher" ? null : "вы ученик";
}

/** Какие действия есть у строки. */
export interface RowActions {
  /** Ссылка «Архив»: только у урока со снимком доски. */
  archive: boolean;
  /** Шеврон развёртки: без него у строки нет ничего, кроме ряда действий. */
  hasMore: boolean;
  /** Кнопка «Ссылка» (скопировать ссылку для преподавателей). */
  guestLink: boolean;
  /** «Завершить» в развёртке. */
  end: boolean;
  /** Слова под шагом подтверждения «В корзину», либо `null`: подтверждение без слов. */
  trashConfirm: string | null;
}

/**
 * Действия строки списка.
 *
 * У звонка:
 *
 * - **архива нет никогда**, даже если сервер прислал снимок: нужда `archive` у
 *   звонка отвечает 404 (Р5 проекта), сервер и так шлёт `hasBoardSnapshot:
 *   false`, а здесь это повторено, чтобы ссылка не вела в отказ и у старого API;
 * - **у гостя развёртки нет**: у ученика её заводила только ссылка «Архив», а
 *   действий над звонком у гостя нет ни одного;
 * - **«Завершить» в строке нет**, его делает «В корзину» (6.1 проекта: у звонка
 *   оба пути это один конец), и делает с подтверждением. Кнопка «Завершить» в
 *   строке подтверждения не имеет, а конец звонка необратим: он стирает
 *   содержимое через срок корзины;
 * - **подтверждение корзины со словами** о том, что будет (9.2 проекта);
 * - **«Ссылки» нет в просмотре кабинета админом** (`viewing`): выдача ключа
 *   это запись, замок записи ответит `403 view_only`, и кнопка вела бы в
 *   отказ. Остальное в строке просмотру не прячется, как и у урока: пишущие
 *   кнопки кабинета отвечают полосой просмотра.
 */
export function rowActionsOf(
  lesson: Pick<Lesson, "myRole" | "call" | "status" | "hasBoardSnapshot">,
  opts: { viewing?: boolean } = {},
): RowActions {
  const ended = lesson.status === "ended";
  if (lesson.call !== undefined) {
    const host = lesson.call.role === "host";
    return {
      archive: false,
      hasMore: host,
      guestLink: host && !ended && opts.viewing !== true,
      end: false,
      trashConfirm: host ? callEndConfirmText() : null,
    };
  }
  const teacher = lesson.myRole === "teacher";
  const archive = lesson.hasBoardSnapshot === true;
  return {
    archive,
    // У ученика в развёртке бывает только архив незавершённого урока
    hasMore: teacher || (!ended && archive),
    guestLink: false,
    end: teacher && !ended,
    trashConfirm: null,
  };
}

/**
 * Подтверждение конца звонка: кнопка «В корзину» в строке списка и
 * «Завершить» в комнате говорят одно и то же (9.2 проекта).
 *
 * ⚠️ Срок не пишется числом на глаз: он берётся из `TRASH_KEEP_DAYS`, зеркала
 * срока корзины на сервере. Содержимое завершённого звонка стирает уборка
 * корзины (П7 проекта), то есть срок у них один.
 */
export function callEndConfirmText(): string {
  return (
    "Звонок завершится для всех, и вернуться в него будет нельзя. " +
    `Доска и ответы удалятся через ${dayCountText(TRASH_KEEP_DAYS)}, в архив звонок не попадёт.`
  );
}

/**
 * Отказ выдачи ссылки словами.
 *
 * 404 у ведущего это конец звонка либо снятое право. `view_only` (просмотр
 * кабинета, кнопку в нём прячет `rowActionsOf`, но вкладка могла уйти в
 * просмотр позже) говорит словами самого отказа: совет «попробовать ещё раз»
 * там неправда, повтор отказывает так же. Отказ читается структурно, как в
 * `call-gate-rules.ts`: модуль не тянет клиент API и проверяется `node:test`.
 */
export function guestLinkErrorText(e: unknown): string {
  const code = typeof e === "object" && e !== null ? (e as { code?: unknown }).code : undefined;
  if (code === "view_only" && e instanceof Error) return e.message;
  if (code === "lesson_not_found") {
    return "Ссылки нет: звонок уже завершён или заводить звонки вам больше нельзя.";
  }
  return "Не удалось получить ссылку. Попробуйте ещё раз.";
}

/**
 * Отказ `lesson_started` у обновления материала из заготовки. У урока это
 * «ученик уже заходил», у звонка в комнату заходили участники.
 */
export function startedText(call: boolean): string {
  return call
    ? "В звонок уже заходили: обновить материал нельзя"
    : "Ученик уже заходил на этот урок: обновлять его нельзя";
}

/**
 * Состояние связи с заготовкой у звонка. «На уроке» и «занятие уже было» про
 * звонок неточны: у звонка правят доску в звонке, а закрывает обновление то,
 * что в звонок уже заходили.
 */
export function planDivergedText(call: boolean): string {
  return call ? "изменён в звонке" : "изменён на уроке";
}

export function planClosedText(call: boolean): string {
  return call ? "в звонок уже заходили, обновление закрыто" : "занятие уже было, обновление закрыто";
}

/** Слова для счёта ответов на доске, которые затрёт обновление из заготовки. */
export function answersLossLabel(call: boolean): string {
  return call ? "ответов участников" : "ответов ученика";
}
